import { useGoogleAnalyticsData } from "@/hooks/useGoogleAnalyticsData";
import { useGoogleSearchConsoleData } from "@/hooks/useGoogleSearchConsoleData";
import { auth } from "@/lib/firebase";
import { Domain } from "@/schemas";
import { useStore } from "@/store";
import { onAuthStateChanged } from "firebase/auth";
import { useEffect, useMemo } from "react";

/**
 * Hook to initialize the Zustand store with Firebase authentication
 * and set up necessary subscriptions for all data.
 *
 * This simplified version subscribes to all domains, pages, and jobs when a user logs in
 * and maintains these subscriptions throughout the app lifecycle.
 */
export const useStoreInitializer = () => {
  // Current state selectors
  const currentUser = useStore((state) => state.currentUser);
  const orgId = useStore((state) => state.orgId);
  const domains = useStore((state) => state.domains);
  const currentDomainId = useStore((state) => state.userData?.currentDomainId);
  const currentPageId = useStore((state) => state.userData?.currentPageId);
  const pendingWorkspaceSelection = useStore(
    (state) => state.pendingWorkspaceSelection,
  );

  // Create stable userId for Google Analytics subscription
  const userId = useMemo(() => currentUser?.uid || null, [currentUser?.uid]);

  // Initialize Google Analytics data subscription
  useGoogleAnalyticsData(userId);
  useGoogleSearchConsoleData(userId);

  // Memoize actions to ensure stable references
  const setCurrentUser = useStore((state) => state.setCurrentUser);
  const fetchMyOrganization = useStore((state) => state.fetchMyOrganization);
  const clearOrganization = useStore((state) => state.clearOrganization);
  const subscribeToMembers = useStore((state) => state.subscribeToMembers);
  const subscribeToUserData = useStore((state) => state.subscribeToUserData);
  const subscribeToDomains = useStore((state) => state.subscribeToDomains);
  const subscribeToPages = useStore((state) => state.subscribeToPages);
  const subscribeToJobs = useStore((state) => state.subscribeToJobs);
  const subscribeToDomainIntegrations = useStore(
    (state) => state.subscribeToDomainIntegrations,
  );
  const subscribeToPageIntegrations = useStore(
    (state) => state.subscribeToPageIntegrations,
  );
  const subscribeToWpIntegration = useStore(
    (state) => state.subscribeToWpIntegration,
  );
  const unsubscribeWpIntegration = useStore(
    (state) => state.unsubscribeWpIntegration,
  );
  const subscribeToWpSites = useStore((state) => state.subscribeToWpSites);
  const unsubscribeWpSites = useStore((state) => state.unsubscribeWpSites);
  const subscribeToWpCategories = useStore(
    (state) => state.subscribeToWpCategories,
  );
  const unsubscribeWpCategories = useStore(
    (state) => state.unsubscribeWpCategories,
  );
  const unsubscribeDomains = useStore((state) => state.unsubscribeDomains);
  const unsubscribePages = useStore((state) => state.unsubscribePages); // Ensure this is selected
  const unsubscribeJobs = useStore((state) => state.unsubscribeJobs);
  const unsubscribeDomainIntegrations = useStore(
    (state) => state.unsubscribeDomainIntegrations,
  );
  const unsubscribePageIntegrations = useStore(
    (state) => state.unsubscribePageIntegrations,
  );
  const subscribeToPersonas = useStore((state) => state.subscribeToPersonas);
  const subscribeToOffers = useStore((state) => state.subscribeToOffers);
  const subscribeToPolicies = useStore((state) => state.subscribeToPolicies);
  const subscribeToIdeas = useStore((state) => state.subscribeToIdeas);
  const unsubscribePersonas = useStore((state) => state.unsubscribePersonas);
  const unsubscribeOffers = useStore((state) => state.unsubscribeOffers);
  const unsubscribePolicies = useStore((state) => state.unsubscribePolicies);
  const unsubscribeIdeas = useStore((state) => state.unsubscribeIdeas);

  // Actions to set the current domain/page objects based on IDs
  const setCurrentDomain = useStore((state) => state.setCurrentDomain);
  const setCurrentPage = useStore((state) => state.setCurrentPage);
  const setCurrentDomainIntegrations = useStore(
    (state) => state.setCurrentDomainIntegrations,
  );
  const setCurrentPageIntegrations = useStore(
    (state) => state.setCurrentPageIntegrations,
  );
  const clearPendingWorkspaceSelection = useStore(
    (state) => state.clearPendingWorkspaceSelection,
  );

  // Effect for Auth State and All Subscriptions
  useEffect(() => {
    console.log("Auth effect running - simplified subscription model");

    const unsubscribeAuth = onAuthStateChanged(auth, (user) => {
      console.log("Auth state changed:", user?.uid);
      setCurrentUser(user);

      if (user) {
        // Subscribe to all data at once when user logs in
        console.log("User logged in, subscribing to all data for:", user.uid);

        // Subscribe to user data
        const unsubUser = subscribeToUserData(user.uid);

        // Subscribe to user-level WP sites (still user-scoped, unaffected
        // by org membership)
        const unsubWpSites = subscribeToWpSites(user.uid);

        // Resolve the caller's organization. Domain-scoped subscriptions
        // (domains, pages, jobs, ...) are gated on orgId being resolved --
        // see the effect below.
        void fetchMyOrganization();

        // Return cleanup function for all subscriptions
        return () => {
          console.log("Cleaning up all subscriptions");
          unsubUser();
          unsubscribeDomains();
          unsubscribePages();
          unsubscribeJobs();
          unsubscribeDomainIntegrations();
          unsubscribePageIntegrations();
          unsubscribePersonas();
          unsubscribeOffers();
          unsubscribePolicies();
          unsubscribeIdeas();
          unsubscribeWpIntegration();
          unsubscribeWpCategories();
          unsubWpSites();
        };
      } else {
        // User logged out, clear all subscriptions
        console.log("User logged out, unsubscribing from everything");
        clearOrganization();
        unsubscribeDomains();
        unsubscribePages();
        unsubscribeJobs();
        unsubscribeDomainIntegrations();
        unsubscribePageIntegrations();
        unsubscribePersonas();
        unsubscribeOffers();
        unsubscribePolicies();
        unsubscribeIdeas();
        unsubscribeWpIntegration();
        unsubscribeWpCategories();
        unsubscribeWpSites();
      }
    });

    // Cleanup auth listener on component unmount
    return () => {
      console.log("Cleaning up auth listener and all subscriptions");
      unsubscribeAuth();
      // Ensure all subscriptions are cleaned up on final unmount
      if (auth.currentUser) {
        unsubscribeDomains();
        unsubscribePages();
        unsubscribeJobs();
        unsubscribeDomainIntegrations();
        unsubscribePageIntegrations();
        unsubscribePersonas();
        unsubscribeOffers();
        unsubscribePolicies();
        unsubscribeIdeas();
        unsubscribeWpIntegration();
        unsubscribeWpCategories();
        unsubscribeWpSites();
      }
    };
  }, [
    setCurrentUser,
    fetchMyOrganization,
    clearOrganization,
    subscribeToUserData,
    subscribeToWpSites,
    unsubscribeDomains,
    unsubscribePages,
    unsubscribeJobs,
    unsubscribeDomainIntegrations,
    unsubscribePageIntegrations,
    unsubscribePersonas,
    unsubscribeOffers,
    unsubscribePolicies,
    unsubscribeIdeas,
    unsubscribeWpIntegration,
    unsubscribeWpCategories,
    unsubscribeWpSites,
  ]);

  // Subscribe to domains once the caller's organization is resolved. A user
  // with no organization (hasCheckedOrg true, orgId null) simply gets no
  // domains -- they're gated to the create/join org screen elsewhere.
  //
  // Keyed on the signed-in uid as well as the org, and that is load-bearing:
  // Firestore treats permission-denied on a listen as terminal, so a listener
  // attached while signed out never recovers on its own. Without the uid in the
  // deps, a sign-out/sign-in in the same tab left a dead listener behind --
  // `fetchMyOrganization()` from the *previous* session could resolve after
  // sign-out and write the old orgId back, the effect would attach a listener
  // with no auth, and the next sign-in resolved the same orgId, so the effect
  // never re-ran. The symptom was an empty domain list (and, for an admin, the
  // setup wizard) until a full page reload.
  const currentUserId = currentUser?.uid ?? null;
  useEffect(() => {
    if (!currentUserId || !orgId) {
      unsubscribeDomains();
      return;
    }
    const unsubDomains = subscribeToDomains(orgId);
    // App-wide, not per-page: the roster resolves the uids in the activity feed
    // as well as filling the members list on the org settings page.
    const unsubMembers = subscribeToMembers(orgId);
    return () => {
      unsubDomains();
      unsubMembers();
    };
  }, [
    currentUserId,
    orgId,
    subscribeToDomains,
    unsubscribeDomains,
    subscribeToMembers,
  ]);

  // Effect to update current domain and page objects when IDs change.
  // Depends on `domains` too: setCurrentDomain resolves the id against the
  // loaded domains list, and that list arrives asynchronously -- without the
  // dep, a cold load resolves before domains land, leaves currentDomain null
  // and never retries.
  useEffect(() => {
    if (currentUser && orgId) {
      // Set current domain object based on ID
      console.log(`Setting current domain object for ID: ${currentDomainId}`);
      setCurrentDomain(currentDomainId ?? null); // Handle potential undefined

      // Set current page object based on ID
      console.log(`Setting current page object for ID: ${currentPageId}`);
      setCurrentPage(currentPageId ?? null); // Handle potential undefined

      // Always subscribe to pages for the current domain
      if (currentDomainId) {
        console.log(`Subscribing to pages for domain: ${currentDomainId}`);
        subscribeToPages(orgId, currentDomainId);

        // Subscribe to personas, offers, and policies for the current domain
        // This ensures immediate subscription even for newly created domains
        console.log(
          `Subscribing to personas, offers, and policies for domain: ${currentDomainId}`,
        );
        subscribeToPersonas(orgId, currentDomainId);
        subscribeToOffers(orgId, currentDomainId);
        subscribeToPolicies(orgId, currentDomainId);
        subscribeToIdeas(orgId, currentDomainId);

        // Subscribe to domain integrations for the current domain
        console.log(
          `Subscribing to domain integrations for domain: ${currentDomainId}`,
        );
        subscribeToDomainIntegrations(orgId, currentDomainId);
        setCurrentDomainIntegrations(currentDomainId);

        // Subscribe to WP domain integration
        subscribeToWpIntegration(orgId, currentDomainId);

        // Subscribe to WP categories for the current domain
        subscribeToWpCategories(orgId, currentDomainId);

        // Subscribe to all jobs for the domain (collection group query)
        console.log(
          `Subscribing to jobs for domain ${currentDomainId}`,
        );
        subscribeToJobs(currentDomainId);

        // Subscribe to page integrations for the current page (if set)
        if (currentPageId) {
          console.log(
            `Subscribing to page integrations for domain ${currentDomainId}, page ${currentPageId}`,
          );
          subscribeToPageIntegrations(
            orgId,
            currentDomainId,
            currentPageId,
          );
          setCurrentPageIntegrations(currentPageId);
        }
      }
    }
  }, [
    currentUser,
    orgId,
    domains,
    currentDomainId,
    currentPageId,
    setCurrentDomain,
    setCurrentPage,
    setCurrentDomainIntegrations,
    setCurrentPageIntegrations,
    subscribeToPages,
    subscribeToPersonas,
    subscribeToOffers,
    subscribeToPolicies,
    subscribeToIdeas,
    subscribeToJobs,
    subscribeToDomainIntegrations,
    subscribeToPageIntegrations,
    subscribeToWpIntegration,
    subscribeToWpCategories,
  ]);

  useEffect(() => {
    if (!pendingWorkspaceSelection) {
      return;
    }

    const hasDomain = domains.some(
      (domain) => domain.id === pendingWorkspaceSelection.domainId,
    );

    if (
      currentDomainId === pendingWorkspaceSelection.domainId &&
      hasDomain
    ) {
      clearPendingWorkspaceSelection();
    }
  }, [
    pendingWorkspaceSelection,
    currentDomainId,
    domains,
    clearPendingWorkspaceSelection,
  ]);

  // Keep subscriptions additive as domains appear. Avoid tearing down all
  // domain data on every domains snapshot update, which causes visible churn.
  useEffect(() => {
    if (currentUser && orgId && domains.length > 0) {
      domains.forEach((domain: Domain) => {
        subscribeToPages(orgId, domain.id);
        subscribeToPersonas(orgId, domain.id);
        subscribeToOffers(orgId, domain.id);
        subscribeToPolicies(orgId, domain.id);
        subscribeToIdeas(orgId, domain.id);
        subscribeToJobs(domain.id);
      });
    } else if (!currentUser) {
      // Ensure all are unsubscribed if user logs out while this effect is active
      console.log(
        "User logged out, ensuring domain data subscriptions are cleared.",
      );
      unsubscribePages();
      unsubscribePersonas();
      unsubscribeOffers();
      unsubscribePolicies();
      unsubscribeIdeas();
    }
    // Note: We don't need a cleanup that runs *only* on unmount here,
    // because the main auth effect already handles unsubscribing everything on logout/unmount.
  }, [
    currentUser,
    orgId,
    domains,
    subscribeToPages,
    subscribeToPersonas,
    subscribeToOffers,
    subscribeToPolicies,
    subscribeToIdeas,
    subscribeToJobs,
    unsubscribePages,
    unsubscribePersonas,
    unsubscribeOffers,
    unsubscribePolicies,
    unsubscribeIdeas,
  ]);

  // This hook doesn't render anything, it just manages store initialization
};
